import React, {FC} from 'react';
import {format} from 'date-fns';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import {ArrowPathIcon, PencilSquareIcon, TrashIcon} from '@heroicons/react/24/outline';
import {deleteOrganizationInvitation, IOrganizationInvitation, resendOrganizationInvitation, VerdocsEndpoint} from '@verdocs/js-sdk';
import {invalidateAllQueries, useOrganizationInvites} from '../../../react-sdk/queries';
import {Badge, Card, RowAction, SectionTitle, Spinner} from '../../../Components';
import {getCallerAccess, roleBadge} from './memberAccess';
import {EditInviteDialog} from '../../../Dialogs';
import {AppState} from '../../../AppState';

export const InvitesTable: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id;
  const {isAdmin, canManageRoles} = getCallerAccess();

  const [working, setWorking] = React.useState(false);
  const [showDeclined, setShowDeclined] = React.useState(false);
  const [editing, setEditing] = React.useState<IOrganizationInvitation | null>(null);
  const invitesQuery = useOrganizationInvites(organizationId);
  const invites = invitesQuery.data || [];
  const visibleInvites = showDeclined ? invites : invites.filter((invite) => invite.status === 'pending');

  const handleDelete = (email: string) => {
    setWorking(true);
    deleteOrganizationInvitation(VerdocsEndpoint.getDefault(), email)
      .then(() => {
        setWorking(false);
        toast.success('The invitation has been deleted.');
        invalidateAllQueries();
      })
      .catch((e) => {
        setWorking(false);
        console.log('Delete error', e);
        toast.error(e.response?.data?.error || 'There was an error deleting the invitation. Please try again later.');
      });
  };

  const handleResend = (email: string) => {
    setWorking(true);
    resendOrganizationInvitation(VerdocsEndpoint.getDefault(), email)
      .then(() => {
        setWorking(false);
        toast.success('The invitation has been resent.');
        invalidateAllQueries();
      })
      .catch((e) => {
        setWorking(false);
        console.log('Resend error', e);
        toast.error(e.response?.data?.error || 'There was an error resending the invitation. Please try again later.');
      });
  };

  if (invitesQuery.isLoading) {
    return <Spinner mode="dark" />;
  }

  return (
    <>
      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <SectionTitle className="mb-0">Invites</SectionTitle>
          <label className="flex w-fit cursor-pointer items-center gap-2 text-[12px] text-gray-500">
            <input
              type="checkbox"
              checked={showDeclined}
              onChange={(e) => setShowDeclined(e.target.checked)}
              className="h-4 w-4 rounded border border-[#dcdee5] accent-[#654dcb]"
            />
            Show declined
          </label>
        </div>

        {!visibleInvites.length ? (
          <div className="py-6 text-center text-[13px] text-gray-500">
            {showDeclined
              ? 'You have no pending or declined invites. Click Invite member to add one now.'
              : 'You have no pending invites. Click Invite member to add one now.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#ebecf0] text-[11px] uppercase tracking-wide text-gray-400">
                  <th className="py-1.5 text-left font-medium">Invitee</th>
                  <th className="py-1.5 pl-3 text-left font-medium">Role</th>
                  <th className="py-1.5 pl-3 text-left font-medium">Status</th>
                  <th className="hidden py-1.5 pl-3 text-left font-medium tablet:table-cell">Sent</th>
                  <th className="py-1.5 pl-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleInvites.map((invite) => {
                  const declined = invite.status !== 'pending';
                  const role = roleBadge([invite.role]);
                  const name = [invite.first_name, invite.last_name].filter(Boolean).join(' ');

                  return (
                    <tr key={invite.email} className="border-b border-[#f2f3f5] last:border-0">
                      <td className="py-2.5">
                        <div className={`text-[13px] font-medium ${declined ? 'text-gray-400' : 'text-[#33364b]'}`}>
                          {name || invite.email}
                        </div>
                        {name && <div className="truncate text-[12px] text-gray-500">{invite.email}</div>}
                      </td>
                      <td className="py-2.5 pl-3">{role && <Badge tone={role.tone}>{role.label}</Badge>}</td>
                      <td className="py-2.5 pl-3">
                        {declined ? (
                          <Badge
                            tone="warn"
                            title="This person declined the invitation. To prevent nuisance invite-spam, they may not be re-invited. If you believe this is in error, please contact support@verdocs.com.">
                            Declined
                          </Badge>
                        ) : (
                          <Badge tone="muted">Pending</Badge>
                        )}
                      </td>
                      <td className="hidden whitespace-nowrap py-2.5 pl-3 text-[12px] text-gray-500 tablet:table-cell">
                        {format(new Date(invite.generated_at), 'MMM d, yyyy h:mm a')}
                      </td>
                      <td className="py-2.5 pl-3">
                        <div className="flex justify-end gap-0.5">
                          {isAdmin && !declined && (
                            <RowAction title="Resend invitation" disabled={working} onClick={() => handleResend(invite.email)}>
                              <ArrowPathIcon />
                            </RowAction>
                          )}
                          {!declined && canManageRoles([invite.role]) && (
                            <RowAction title="Edit role" disabled={working} onClick={() => setEditing(invite)}>
                              <PencilSquareIcon />
                            </RowAction>
                          )}
                          {canManageRoles([invite.role]) && (
                            <RowAction title="Delete invitation" danger disabled={working} onClick={() => handleDelete(invite.email)}>
                              <TrashIcon />
                            </RowAction>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <EditInviteDialog
          invite={editing}
          onClose={() => {
            setEditing(null);
            invitesQuery.refetch();
          }}
        />
      )}

      {working && (
        <div className="absolute inset-0 bg-[#0000007f] flex flex-col items-center pt-8 text-white font-lg font-semibold">
          <Spinner mode="light" />
          <div className="mt-4">Please wait...</div>
        </div>
      )}
    </>
  );
});
